import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

import { layersAtom, type LayerData } from '@/atoms/layers.atom';

export interface SourceLayerPick {
  /** Every layer the picker offers (accepted project layers; the initial
   *  layer first when it is not in the project list yet). */
  candidates: LayerData[];
  /** The effective source: the pick (initially the initial layer) while it is
   *  still a candidate, else the first candidate that is not `avoidId`, else
   *  the first candidate. Undefined when there is none. */
  sourceLayer: LayerData | undefined;
  sourceLayerId: string;
  setSourceLayerId: (id: string) => void;
}

/**
 * Source-layer selection for the analysis dialogs (D10).
 *
 * Derived, not synchronised: the effective source is computed on every render
 * from the pick and the live layer list, so a layer deleted while the dialog
 * is open falls back to the next candidate without an effect.
 *
 * `accepts` MUST be a module-level predicate (e.g. `isPointcloudLayer`) — it
 * is a `useMemo` dependency.
 */
export function useSourceLayerPick(
  accepts: (layer: LayerData) => boolean,
  initial?: LayerData,
  avoidId?: string,
): SourceLayerPick {
  const allLayers = useAtomValue(layersAtom);
  const candidates = useMemo(() => {
    const base = allLayers.filter(accepts);
    return initial && !base.some((l) => l.id === initial.id) ? [initial, ...base] : base;
  }, [allLayers, accepts, initial]);
  const [picked, setPicked] = useState<string | null>(initial?.id ?? null);
  const fallback = candidates.find((l) => l.id !== avoidId) ?? candidates[0];
  const sourceLayer = (picked ? candidates.find((l) => l.id === picked) : undefined) ?? fallback;
  return { candidates, sourceLayer, sourceLayerId: sourceLayer?.id ?? '', setSourceLayerId: setPicked };
}
